import type { Editor } from '@tiptap/react';

/**
 * Moves the caret into an existing comment thread and scrolls it into view.
 *
 * Deliberately collapses to a point *inside* the mark rather than selecting the whole
 * range: every surface that shows a thread keys off a collapsed selection sitting
 * inside a `comment` mark, so this is what "open that one" means everywhere.
 *
 * Focus stays where it is. Picking a card out of a list puts the caret in the thread —
 * which is what drives the highlight and the open card — without moving focus out of
 * the list someone is reading down, where a stray keystroke would land in the prose.
 */
export function scrollToCommentThread(
  editor: Editor,
  commentId: string
): boolean {
  let target: { from: number; to: number } | null = null;

  editor.state.doc.descendants((node, pos) => {
    if (target !== null || !node.isText) {
      return;
    }
    const hasMark = node.marks.some(
      (mark) =>
        mark.type.name === 'comment' && mark.attrs['commentId'] === commentId
    );
    if (hasMark) {
      target = { from: pos, to: pos + node.nodeSize };
    }
  });

  if (target === null) {
    return false;
  }

  const { from, to } = target;
  // One character in, so the cursor lands inside the (non-inclusive) mark rather than
  // exactly on its boundary — landing on the boundary can resolve to the unmarked side.
  const cursorPos = to > from + 1 ? from + 1 : from;

  editor.chain().setTextSelection(cursorPos).scrollIntoView().run();

  return true;
}
